import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "@/lib/trpc";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Bug, Lightbulb, HelpCircle, AlertCircle, MessageSquare, Loader2 } from "lucide-react";

interface FeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const feedbackTypes = [
  { value: "bug", icon: Bug, labelKey: "feedback.type_bug" },
  { value: "suggestion", icon: Lightbulb, labelKey: "feedback.type_suggestion" },
  { value: "question", icon: HelpCircle, labelKey: "feedback.type_question" },
  { value: "complaint", icon: AlertCircle, labelKey: "feedback.type_complaint" },
  { value: "other", icon: MessageSquare, labelKey: "feedback.type_other" },
];

export default function FeedbackModal({ open, onOpenChange }: FeedbackModalProps) {
  const { t } = useTranslation();
  const [type, setType] = useState<string>("suggestion");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  const createFeedback = trpc.feedback.create.useMutation({
    onSuccess: () => {
      toast.success(t("feedback.success_message"));
      // Reset form
      setType("suggestion");
      setSubject("");
      setMessage("");
      onOpenChange(false);
    },
    onError: (error) => {
      toast.error(t("feedback.error_message"));
      console.error("Failed to submit feedback:", error);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!type || !subject.trim() || !message.trim()) {
      toast.error(t("feedback.validation_error"));
      return;
    }

    createFeedback.mutate({
      type: type as "bug" | "suggestion" | "question" | "complaint" | "other",
      subject: subject.trim(),
      message: message.trim(),
      pageUrl: window.location.href,
      userAgent: navigator.userAgent,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("feedback.title")}</DialogTitle>
          <DialogDescription>{t("feedback.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="type">{t("feedback.type_label")}</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {feedbackTypes.map((feedbackType) => {
                  const Icon = feedbackType.icon;
                  return (
                    <SelectItem key={feedbackType.value} value={feedbackType.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        {t(feedbackType.labelKey)}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="space-y-2">
            <Label htmlFor="subject">{t("feedback.subject_label")}</Label>
            <Input
              id="subject"
              placeholder={t("feedback.subject_placeholder")}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
              required
            />
            <p className="text-xs text-muted-foreground">
              {subject.length}/200
            </p>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <Label htmlFor="message">{t("feedback.message_label")}</Label>
            <Textarea
              id="message"
              placeholder={t("feedback.message_placeholder")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              maxLength={5000}
              required
            />
            <p className="text-xs text-muted-foreground">
              {message.length}/5000
            </p>
          </div>

          {/* Info Note */}
          <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
            <p>{t("feedback.info_note")}</p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createFeedback.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={createFeedback.isPending}>
              {createFeedback.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("feedback.submit_button")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
